import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { OPP_STATUS, clp } from "@/lib/format";
import { Pick } from "./Sales";

const NEXT = {
  borrador: ["cancelada"], cotizada: ["en_seguimiento", "adjudicada", "no_adjudicada", "cancelada"],
  en_seguimiento: ["adjudicada", "no_adjudicada", "cancelada"], no_adjudicada: ["en_seguimiento"], cancelada: ["en_seguimiento"], adjudicada: [],
};

export const nextStatuses = (s) => NEXT[s] || [];

export const QuoteDialog = ({ open, onOpenChange, oppId, base, onSaved }) => {
  const blank = { description: "", qty: 1, unit: "UN", unit_price: 0 };
  const [items, setItems] = useState(base?.items?.map(({ total, ...i }) => i) || [blank]);
  const [meta, setMeta] = useState({ budget_cost: base?.budget_cost || 0, validity_days: base?.validity_days || 30, notes: base?.notes || "" });
  const net = items.reduce((s, i) => s + Math.round((Number(i.qty) || 0) * (Number(i.unit_price) || 0)), 0);
  const setItem = (idx, k, v) => setItems(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const save = async () => {
    try {
      const { data } = await api.post(`/gerencia/opportunities/${oppId}/quotes`, { ...meta, budget_cost: Number(meta.budget_cost), validity_days: Number(meta.validity_days),
        items: items.map((i) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price) })) });
      toast.success(`Cotización v${data.version} registrada`); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader><DialogTitle>Nueva versión de cotización</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_70px_70px_110px_32px] gap-2 items-center">
              <Input data-testid={`quote-item-desc-${idx}`} placeholder="Descripción" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} />
              <Input data-testid={`quote-item-qty-${idx}`} type="number" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} />
              <Input data-testid={`quote-item-unit-${idx}`} value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} />
              <Input data-testid={`quote-item-price-${idx}`} type="number" placeholder="P. unit." value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} />
              <button data-testid={`quote-item-remove-${idx}`} disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-[#8a9584] hover:text-[#b42318]"><Trash2 size={15} /></button>
            </div>
          ))}
          <Button data-testid="quote-add-item-btn" size="sm" variant="ghost" onClick={() => setItems([...items, blank])}><Plus size={14} className="mr-1" />Agregar ítem</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Presupuesto (costo estimado, neto)"><Input data-testid="quote-budget-input" type="number" value={meta.budget_cost} onChange={(e) => setMeta({ ...meta, budget_cost: e.target.value })} /></Field>
          <Field label="Validez (días)"><Input data-testid="quote-validity-input" type="number" value={meta.validity_days} onChange={(e) => setMeta({ ...meta, validity_days: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Observaciones"><Textarea data-testid="quote-notes-input" value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} /></Field></div>
        </div>
        <div className="rounded-lg bg-[#f6fbf2] p-3 text-sm flex flex-wrap gap-4" data-testid="quote-preview-totals">
          <span>Neto <b>{clp(net)}</b></span><span>IVA 19% <b>{clp(Math.round(net * 0.19))}</b></span><span>Total <b>{clp(net + Math.round(net * 0.19))}</b></span>
          <span className="text-[#5b6656]">Margen estimado <b>{clp(net - Number(meta.budget_cost || 0))}</b></span>
        </div>
        <DialogFooter><Button data-testid="quote-save-btn" className="rounded-full" onClick={save}>Guardar versión</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StatusDialog = ({ target, onOpenChange, oppId, onSaved, reasons }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    try { await api.post(`/gerencia/opportunities/${oppId}/status`, { status: target, reason: reason || null, note }); toast.success(`Estado: ${OPP_STATUS[target].label}`); setNote(""); setReason(""); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent data-testid="status-dialog">
        <DialogHeader><DialogTitle>Cambiar a {target && OPP_STATUS[target].label}</DialogTitle></DialogHeader>
        {target === "no_adjudicada" && <Field label="Motivo *"><Pick testid="lost-reason-select" value={reason} onChange={setReason} options={reasons} placeholder="Seleccione motivo" /></Field>}
        <Field label={target === "cancelada" ? "Motivo de cancelación *" : "Observación"}><Textarea data-testid="status-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="status-confirm-btn" className="rounded-full" onClick={save}>Confirmar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
